import React from 'react';
import { motion } from 'framer-motion';

/**
 * Milestone-inspired 3D-tilted device showcase.
 * - iPad-style bezel built in pure CSS (rounded-3xl + dark bezel + speaker cutout)
 * - CSS 3D transform: rotateY ~14deg, rotateZ ~-7deg + perspective
 * - Floats with a subtle continuous y-axis bobble
 * - Decorative SVG ornaments (gold star + squiggle) around the device
 * - Soft pastel emerald + gold gradient blobs behind it
 */
export const IpadShowcase = () => (
  <section id="ipad" className="relative py-28 md:py-40 overflow-hidden" data-testid="landing-ipad-showcase">
    {/* Pastel gradient backdrop blobs (emerald + gold, mirrors hero palette) */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute top-1/4 left-[-10%] w-[55vw] h-[55vw] rounded-full blur-[120px] opacity-25"
        style={{ background: 'radial-gradient(closest-side, #A8D5BA 0%, transparent 70%)' }} />
      <div className="absolute bottom-[10%] right-[-12%] w-[60vw] h-[60vw] rounded-full blur-[140px] opacity-20"
        style={{ background: 'radial-gradient(closest-side, #E8D29A 0%, transparent 70%)' }} />
    </div>

    <div className="relative mx-auto max-w-7xl px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
        className="max-w-3xl mb-16 md:mb-20"
      >
        <div className="text-[10px] md:text-xs uppercase tracking-[0.32em] text-white/45 font-semibold mb-5">The Console</div>
        <h2 className="font-display text-white font-semibold tracking-tight text-4xl md:text-6xl leading-[1.02]">
          See every leak,
          <span className="block italic bg-clip-text text-transparent bg-gradient-to-r from-[#A8D5BA] to-[#E8D29A]">on one operator-grade dashboard.</span>
        </h2>
        <p className="text-white/55 text-base md:text-lg mt-6 leading-relaxed">
          POS, books, bank, suppliers, aggregators, mandi prices and reviews — every signal on a single screen, refreshed nightly, with AI variance explainers on every red row.
        </p>
      </motion.div>

      {/* Device stage */}
      <div className="relative flex justify-center items-center" style={{ perspective: '2200px', perspectiveOrigin: '50% 30%' }}>
        {/* Decorative gold star ornament — top-left */}
        <svg aria-hidden="true" viewBox="0 0 64 64"
          className="hidden md:block absolute -top-2 left-[2%] w-10 h-10 lg:w-14 lg:h-14 pointer-events-none">
          <motion.path
            initial={{ scale: 0, rotate: -45, opacity: 0 }}
            whileInView={{ scale: 1, rotate: 0, opacity: 0.85 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            d="M32 4 L36 28 L60 32 L36 36 L32 60 L28 36 L4 32 L28 28 Z"
            fill="#E8D29A"
            style={{ transformOrigin: 'center' }}
          />
        </svg>

        {/* Decorative hand-drawn squiggle — bottom-left */}
        <svg aria-hidden="true" viewBox="0 0 120 240"
          className="hidden md:block absolute bottom-2 left-[3%] w-12 h-24 lg:w-16 lg:h-36 pointer-events-none opacity-50">
          <motion.path
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: 0.8, ease: 'easeOut' }}
            d="M60 8 C 30 40, 90 80, 50 120 S 90 180, 60 232"
            fill="none"
            stroke="#C9A66B"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="3 6"
          />
        </svg>

        {/* The iPad — outer wrapper holds the 3D rotation (no framer-motion to avoid transform conflicts) */}
        <div
          className="relative w-full max-w-[1080px]"
          data-testid="ipad-frame"
          style={{
            transform: 'rotateY(-22deg) rotateZ(-10deg) rotateX(8deg)',
            transformStyle: 'preserve-3d',
            transformOrigin: 'center center',
          }}
        >
          {/* Entrance + continuous bobble */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
            >
            {/* Outer bezel */}
            <div className="relative rounded-[36px] md:rounded-[44px] p-3 md:p-4 bg-gradient-to-br from-[#1a1a1d] via-[#0f0f12] to-[#28282B] border border-white/[0.08]"
              style={{ boxShadow: '0 60px 120px -30px rgba(0,0,0,0.85), 0 30px 60px -20px rgba(168,213,186,0.18), inset 0 1px 0 rgba(255,255,255,0.04)' }}>
              {/* Speaker grille (top) + camera dot */}
              <div className="absolute top-1.5 md:top-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                <span className="block w-14 md:w-20 h-[3px] rounded-full bg-white/[0.06]" />
                <span className="block w-1.5 h-1.5 rounded-full bg-white/15 ring-1 ring-white/5" />
              </div>
              {/* Side buttons */}
              <span aria-hidden="true" className="absolute -right-[3px] top-[12%] w-[3px] h-12 md:h-16 rounded-r-sm bg-gradient-to-b from-[#2a2a2e] to-[#15151a]" />
              <span aria-hidden="true" className="absolute -right-[3px] top-[34%] w-[3px] h-8 md:h-12 rounded-r-sm bg-gradient-to-b from-[#2a2a2e] to-[#15151a]" />
              {/* Inner screen */}
              <div className="relative rounded-[24px] md:rounded-[28px] overflow-hidden bg-[#28282B] aspect-[16/10]">
                {/* Subtle screen highlight */}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.06] via-transparent to-transparent z-10" />
                <img
                  src="/landing/dashboard-mockup.png"
                  alt="Epicurea Nexus Dashboard"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                {/* Glassy reflective sheen — diagonal */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-30 z-20"
                  style={{
                    background: 'linear-gradient(115deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 65%, rgba(255,255,255,0.06) 100%)',
                  }}
                />
              </div>
            </div>

            {/* Floor reflection shadow */}
            <div
              aria-hidden="true"
              className="absolute -bottom-10 left-[8%] right-[8%] h-12 rounded-[50%] blur-3xl opacity-50"
              style={{ background: 'radial-gradient(closest-side, rgba(168,213,186,0.45), transparent 70%)' }}
            />
            </motion.div>
          </motion.div>
        </div>

        {/* Decorative gold sparkle — right side */}
        <svg aria-hidden="true" viewBox="0 0 64 64"
          className="hidden lg:block absolute top-[18%] right-[2%] w-8 h-8 pointer-events-none">
          <motion.path
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 0.7 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 1.1 }}
            d="M32 4 L36 28 L60 32 L36 36 L32 60 L28 36 L4 32 L28 28 Z"
            fill="#A8D5BA"
            style={{ transformOrigin: 'center' }}
          />
        </svg>
      </div>

      {/* Below-device byline */}
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="text-center text-white/40 text-xs md:text-sm mt-20 md:mt-24"
      >
        Live operator console · powered by Petpooja sync · Tally connector · Swiggy / Zomato settlement audit
      </motion.p>
    </div>
  </section>
);

export default IpadShowcase;
